import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { generateQueryParams, isError } from "nhb-toolbox";
import type { QueryObject } from "nhb-toolbox/object/types";
import { httpService, type HTTPResponse } from "src/Helpers/utils/axios";
import type { TQueryTag } from "src/Helpers/utils/constants";
import Toaster from "src/Toaster/Toaster";

export interface IErrorResponse {
	success: boolean;
	message: string;
	type: string;
	status: number;
}

type TQueryKey = TQueryTag | string | number;

/** * Interface for mutation options. */
interface IMutationOptions {
	/** - HTTP method type (POST, PUT, PATCH, DELETE) */
	method: "POST" | "PUT" | "PATCH" | "DELETE";
	/** - API endpoint URL */
	endPoint: string;
	/** - Optional query key for invalidating queries after mutation */
	invalidateQueries?: Array<TQueryKey | TQueryKey[]>;
	/** - Optional params for the API request */
	params?: QueryObject;
}

/**
 * * Custom hook to handle API mutations using tanstack-query.
 *
 * @param T - Type of the request data.
 * @param U - Type of the response data.
 * @param options - Options for the mutation, including method, URL, query key, and params.
 * @param options.method - HTTP method type (POST, PUT, PATCH, DELETE).
 * @param options.endPoint - API endpoint URL.
 * @param options.queryKey - Optional query key for invalidating queries after mutation.
 * @param options.params - Optional params for the API request.
 * @returns useMutation hook for handling API mutations.
 */
export const useApiMutation = <T, U>(options: IMutationOptions) => {
	const Toast = Toaster();
	const queryClient = useQueryClient();

	const { method, endPoint, invalidateQueries, params } = options || {};

	const formattedEndpoint = endPoint + generateQueryParams(params);

	// const mutationFn = async (data: T) => {
	// 	switch (method) {
	// 		case "POST":
	// 			const post = await httpService.post<U, T>({
	// 				url: formattedEndpoint,
	// 				data,
	// 			});
	// 			return post;
	// 		case "PUT":
	// 			const put = await httpService.put<U, T>({
	// 				url: formattedEndpoint,
	// 				data,
	// 			});
	// 			return put;
	// 		case "PATCH":
	// 			const patch = await httpService.patch<U, T>({
	// 				url: formattedEndpoint,
	// 				data,
	// 			});
	// 			return patch;
	// 		case "DELETE":
	// 			const res = await httpService.delete<U>({
	// 				url: formattedEndpoint,
	// 			});
	// 			return res;
	// 		default:
	// 			throw new Error("Unsupported method type!");
	// 	}
	// };

	const mutationFn = async (
		data?: T & { dynamicEndpoint?: string; formData?: FormData }
	) => {
		let url = formattedEndpoint;
		let body: any = data;

		if (data instanceof FormData) {
			body = data;
		} else if (data && typeof data === "object") {
			const { dynamicEndpoint, formData, ...payload } = data as any;
			if (dynamicEndpoint) {
				url = dynamicEndpoint;
			}
			body = formData || payload;
		}

		switch (method) {
			case "POST":
				return await httpService.post<U, any>({ url, data: body });
			case "PUT":
				return await httpService.put<U, any>({ url, data: body });
			case "PATCH":
				return await httpService.patch<U, any>({ url, data: body });
			case "DELETE":
				return await httpService.delete<U>({ url });
			default:
				throw new Error("Unsupported method type!");
		}
	};

	return useMutation<HTTPResponse<U>, Error, T>({
		mutationFn,
		onSuccess: (res) => {
			if (res?.success) {
				Toast.fire({
					icon: "success",
					title: res.message || "Operation was successful!",
				});
			}

			if (invalidateQueries && invalidateQueries.length > 0) {
				invalidateQueries.forEach((key) => {
					queryClient.invalidateQueries({
						queryKey: [key],
						exact: false,
						refetchType: "all",
					});
				});
			}
		},
		onError: (error) => {
			let errorMsg = "Something went wrong!";
			console.log(error);
			if (isAxiosError<IErrorResponse, unknown>(error)) {
				if (error.status === 500) {
					errorMsg = "Something went wrong!";
				}
				if (error.response && error.response.data) {
					errorMsg = error.response?.data?.message;
				}
			} else if (isError(error)) {
				errorMsg = error.message;
			} else {
				errorMsg = "Something went wrong!";
			}

			Toast.fire({
				icon: "error",
				title: errorMsg,
			});
		},
	});
};

// import { useMutation, useQueryClient } from "@tanstack/react-query";
// import { isAxiosError } from "axios";
// import { generateQueryParams, isError } from "nhb-toolbox";
// import type { QueryObject } from "nhb-toolbox/object/types";
// import { httpService, type HTTPResponse } from "src/Helpers/utils/axios";
// import type { TQueryTag } from "src/Helpers/utils/constants";
// import Toaster from "src/Toaster/Toaster";

// export interface IErrorResponse {
// 	success: boolean;
// 	message: string;
// 	type: string;
// 	status: number;
// }

// type TQueryKey = TQueryTag | string | number;

// interface IMutationOptions {
// 	method: "POST" | "PUT" | "PATCH" | "DELETE";
// 	endPoint: string;
// 	invalidateQueries?: Array<TQueryKey | TQueryKey[]>;
// 	params?: QueryObject;
// }

// /**
//  * Custom hook to handle API mutations with dynamic endpoints.
//  * @param options Mutation options.
//  */
// export const useApiMutation = <T, U>(options: IMutationOptions) => {
// 	const Toast = Toaster();
// 	const queryClient = useQueryClient();
// 	const { method, endPoint, invalidateQueries, params } = options || {};

// 	const baseEndpoint = endPoint + generateQueryParams(params);

// 	const mutationFn = async (data: T & { endpoint?: string }) => {
// 		// Use dynamic endpoint if provided, otherwise fallback to baseEndpoint
// 		const url = (data as any)?.endpoint || baseEndpoint;

// 		switch (method) {
// 			case "POST":
// 				return await httpService.post<U, T>({ url, data });
// 			case "PUT":
// 				return await httpService.put<U, T>({ url, data });
// 			case "PATCH":
// 				return await httpService.patch<U, T>({ url, data });
// 			case "DELETE":
// 				return await httpService.delete<U>({ url });
// 			default:
// 				throw new Error("Unsupported method type!");
// 		}
// 	};

// 	return useMutation<HTTPResponse<U>, Error, T>({
// 		mutationFn,
// 		onSuccess: (res) => {
// 			if (res?.success) {
// 				Toast.fire({
// 					icon: "success",
// 					title: res.message || "Operation successful!",
// 				});
// 			}

// 			if (invalidateQueries && invalidateQueries.length > 0) {
// 				invalidateQueries.forEach((key) => {
// 					queryClient.invalidateQueries({
// 						queryKey: [key],
// 						exact: false,
// 						refetchType: "all",
// 					});
// 				});
// 			}
// 		},
// 		onError: (error) => {
// 			let errorMsg = "Something went wrong!";
// 			if (isAxiosError<IErrorResponse, unknown>(error)) {
// 				if (error.response?.data?.message)
// 					errorMsg = error.response.data.message;
// 			} else if (isError(error)) {
// 				errorMsg = error.message;
// 			}
// 			Toast.fire({ icon: "error", title: errorMsg });
// 		},
// 	});
// };
